import { useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { apiJson } from '../api/client.js';
import { useAuth } from '../context/AuthContext.jsx';
import { FieldErrors } from '../components/FieldErrors.jsx';
import { RecoveryCodeList } from '../components/TwoFactorSetupForm.jsx';
import { homePathForUser } from '../utils/roles.js';

export function LoginPage() {
  const { user, booting, login, setSession } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState('credentials');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [challenge, setChallenge] = useState('');
  const [setup, setSetup] = useState(null);
  const [recoveryCodes, setRecoveryCodes] = useState([]);
  const [savedCodes, setSavedCodes] = useState(false);
  const [info, setInfo] = useState('');
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState(/** @type {Record<string, string[]> | undefined} */ (undefined));
  const [submitting, setSubmitting] = useState(false);

  if (!booting && user && step !== 'recovery') {
    return <Navigate to={homePathForUser(user)} replace />;
  }

  const resetToPassword = () => {
    setStep('credentials');
    setCode('');
    setChallenge('');
    setSetup(null);
    setError('');
    setInfo('');
    setSubmitting(false);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setFieldErrors(undefined);
    setSubmitting(true);
    try {
      if (step === 'credentials') {
        const result = await login(email, password);
        if (result?.two_factor || result?.two_factor_setup) {
          setChallenge(result.challenge);
          setInfo(result.message || '');
          setCode('');
          if (result.two_factor_setup) {
            const nextSetup = await apiJson('/api/login/two-factor/setup', {
              method: 'POST',
              body: { challenge: result.challenge },
              skipAuth: true,
            });
            setSetup(nextSetup);
            setStep('setup');
          } else {
            setStep('code');
          }
          return;
        }
        navigate(homePathForUser(result), { replace: true });
        return;
      }

      if (step === 'code') {
        const data = await apiJson('/api/login/two-factor', {
          method: 'POST',
          body: { challenge, code },
          skipAuth: true,
        });
        const signedIn = setSession(data);
        navigate(homePathForUser(signedIn), { replace: true });
        return;
      }

      const data = await apiJson('/api/login/two-factor/confirm', {
        method: 'POST',
        body: { challenge, code },
        skipAuth: true,
      });
      setSession(data);
      setRecoveryCodes(Array.isArray(data.recovery_codes) ? data.recovery_codes : []);
      setSavedCodes(false);
      setStep('recovery');
    } catch (err) {
      setError(err.message);
      setFieldErrors(err.body?.errors);
    } finally {
      setSubmitting(false);
    }
  };

  const continueAfterRecovery = () => {
    if (!user) {
      return;
    }
    navigate(homePathForUser(user), { replace: true });
  };

  return (
    <div className="auth-shell">
      <div className={step === 'credentials' ? 'auth-card' : 'auth-card two-factor'}>
        <h1>{step === 'credentials' ? 'Welcome back' : 'Two-factor authentication'}</h1>
        <p className="muted">
          {step === 'credentials' ? 'Sign in to your client portal' : info || 'Use your authenticator app to continue.'}
        </p>
        {step === 'recovery' ? (
          <div className="stack">
            <p className="muted small" style={{ margin: 0 }}>
              Save these recovery codes. Each code works once if you lose your authenticator app.
            </p>
            <RecoveryCodeList codes={recoveryCodes} />
            <label className="field" style={{ flexDirection: 'row', alignItems: 'center', gap: '0.5rem' }}>
              <input type="checkbox" checked={savedCodes} onChange={(ev) => setSavedCodes(ev.target.checked)} />
              <span>I saved these codes</span>
            </label>
            <button type="button" className="btn-primary" disabled={!savedCodes} onClick={continueAfterRecovery}>
              Continue
            </button>
          </div>
        ) : (
          <form onSubmit={onSubmit} className="stack">
            {step === 'credentials' ? (
              <>
                <label className="field">
                  <span>Email</span>
                  <input
                    type="email"
                    autoComplete="email"
                    value={email}
                    onChange={(ev) => setEmail(ev.target.value)}
                    required
                  />
                </label>
                <label className="field">
                  <span>Password</span>
                  <input
                    type="password"
                    autoComplete="current-password"
                    value={password}
                    onChange={(ev) => setPassword(ev.target.value)}
                    required
                  />
                </label>
              </>
            ) : null}
            {step === 'setup' && setup ? (
              <SetupFields secret={setup.secret} qrDataUri={setup.qr_data_uri} />
            ) : null}
            {step === 'code' || step === 'setup' ? (
              <label className="field">
                <span>{step === 'code' ? 'Authenticator or recovery code' : '6-digit code'}</span>
                <input
                  inputMode={step === 'setup' ? 'numeric' : 'text'}
                  autoComplete="one-time-code"
                  value={code}
                  onChange={(ev) => setCode(ev.target.value)}
                  required
                />
              </label>
            ) : null}
            {error ? <p className="err">{error}</p> : null}
            <FieldErrors errors={fieldErrors} />
            <button type="submit" className="btn-primary" disabled={submitting}>
              {submitting ? 'Checking…' : step === 'credentials' ? 'Sign in' : 'Continue'}
            </button>
            {step !== 'credentials' ? (
              <button type="button" className="btn-secondary" onClick={resetToPassword}>
                Back
              </button>
            ) : null}
          </form>
        )}
        {step === 'credentials' ? (
          <div className="auth-links">
            <Link to="/register">Create an agency account</Link>
            <Link to="/forgot-password">Forgot password?</Link>
          </div>
        ) : null}
      </div>
    </div>
  );
}

function SetupFields({ secret, qrDataUri }) {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(secret);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  return (
    <>
      {qrDataUri ? <img className="two-factor-qr" src={qrDataUri} alt="QR code for authenticator app" /> : null}
      <p className="muted small" style={{ margin: 0 }}>
        Scan the QR code with an authenticator app, or enter this key manually.
      </p>
      <p className="two-factor-secret">{secret}</p>
      <button type="button" className="btn-secondary" onClick={() => void copy()}>
        {copied ? 'Copied' : 'Copy key'}
      </button>
    </>
  );
}
